/**
 * Which deployment this bundle is.
 *
 * Baked in at build time beside `__COMMIT_SHA__`, rather than read from
 * `/health` at runtime. The health check answers only when there is a network
 * and a session, so a runtime value would be absent on the landing page, absent
 * offline, and would arrive a moment after the page did -- three windows in
 * which a dev build looks exactly like production. A warning that is sometimes
 * late is not a warning.
 *
 * The deploy workflows set `ENV_NAME`, and each builds the client separately
 * per environment, so the baked value cannot disagree with where it lands.
 */

/**
 * What this build says it is, defaulting to non-production.
 *
 * The two ways to be wrong are not equal. A production build that forgot to say
 * so wears a warning it does not need -- obvious, embarrassing, fixed within the
 * hour. A development build that forgot to say so is indistinguishable from
 * production, which is the single confusion this whole file exists to prevent.
 * So production is the case that must declare itself, and everything else falls
 * back to `dev`.
 *
 * Read through `typeof`, which is the one way to touch an undeclared identifier
 * without throwing: this module is imported by tests and tools that are not Vite
 * builds and so have no `define` substituting it. Keeping the fallback here
 * rather than only in `vite.config.ts` puts the decision in code that can be
 * read and tested, instead of in build configuration that cannot.
 *
 * Per call rather than once at import, so a test can stub it. Vite replaces the
 * identifier with a literal, so this costs a string comparison and nothing else.
 */
const declared = (): string => (typeof __ENV_NAME__ === 'string' ? __ENV_NAME__ : 'dev');

export const envName = (): string => declared();

export const isProduction = (): boolean => declared() === 'prod';

/**
 * What to call it in a sentence.
 *
 * Named environments get a word; anything else is shown as it was spelled, so a
 * future `staging` or a one-off reads correctly without this list being the
 * thing that has to be updated first.
 */
export function environmentLabel(name: string = envName()): string {
  return name === 'dev' ? 'Development' : name;
}
